/*
Adding an environmental variable
1. Pass in the parser for a variable into parseEnvVars,
 wrapping the parser in optional/required if necessary
2. Put the variable name in the exported object
3. Update lastPass with the new environmental
4. Update test/staging heroku servers with new variables
*/

export const parseString = (value: string) => value;

export const parseFlag = (_: string) => {
  return true as const;
};

export const required =
  <A, B>(f: (value: A) => B) =>
  (value: A | undefined) =>
    value === undefined
      ? new Error("Missing environment variable: ")
      : f(value);

export const optional =
  <A, B>(f: (value: A) => B) =>
  (value: A | undefined) =>
    value === undefined ? undefined : f(value);

function defaultTo<A, B>(f: (value: A) => B | undefined, defaultValue: B) {
  return (value: A | undefined) =>
    value === undefined ? defaultValue : f(value) ?? defaultValue;
}

const parseEnvVars = <T>(parsers: {
  [K in keyof T]: (value: string | undefined) => T[K] | Error;
}): T => {
  const env = { ...(window as any).REACT_ENV, ...process.env };
  const obj = Object.keys(parsers).reduce(
    (acc, varName) => ({
      ...acc,
      [varName]: parsers[varName as keyof T](
        env[varName] as string | undefined,
      ),
    }),
    {},
  ) as { [K in keyof T]: T[K] | Error };

  const errors = Object.keys(parsers)
    .filter(varName => obj[varName as keyof T] instanceof Error)
    .map(varName => (obj[varName as keyof T] as Error).message + varName);
  if (errors.length > 0) {
    throw new Error(errors.join("\n"));
  }

  return obj as T;
};

export enum GraphQLErrorHandlingType {
  Redirect = "redirect",
  Throw = "throw",
  Ignore = "ignore",
}

export enum NodeEnv {
  Production = "production",
  QA = "QA",
  CI = "CI",
  Development = "development",
}

function parseEnum<Enum extends string | number>(enumObj: {
  [K: string]: Enum;
}) {
  const values = Object.values(enumObj);
  return (value: string | undefined) => values.find(v => v === value);
}

export const {
  NODE_ENV,
  REACT_APP_DEBUG_OVERRIDE_GRAPHQL_ERROR_HANDLING_TYPE,
  REACT_APP_SENTRY_DSN,
  REACT_APP_LOB_PUBLIC_KEY,
  REACT_APP_STRIPE_PUBLIC_KEY,
  REACT_APP_PLAID_ENVIRONMENT,
  REACT_APP_PLAID_CLIENT_NAME,
  REACT_APP_PLAID_PUBLIC_KEY,
} = parseEnvVars({
  NODE_ENV: defaultTo(optional(parseEnum(NodeEnv)), NodeEnv.Development),
  REACT_APP_DEBUG_OVERRIDE_GRAPHQL_ERROR_HANDLING_TYPE: optional(
    parseEnum(GraphQLErrorHandlingType),
  ),
  REACT_APP_SENTRY_DSN: optional(parseString),
  REACT_APP_LOB_PUBLIC_KEY: required(parseString),
  REACT_APP_STRIPE_PUBLIC_KEY: required(parseString),
  REACT_APP_PLAID_ENVIRONMENT: required(parseString),
  REACT_APP_PLAID_CLIENT_NAME: required(parseString),
  REACT_APP_PLAID_PUBLIC_KEY: required(parseString),
});

export const REACT_APP_GRAPHQL_ERROR_HANDLING_TYPE =
  REACT_APP_DEBUG_OVERRIDE_GRAPHQL_ERROR_HANDLING_TYPE ??
  GraphQLErrorHandlingType.Redirect;
